import React from "react";
import { Card, CardBody, Button } from "@heroui/react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { BASE_URL } from "../services/config";

const aromaExamples = [
  {
    title: "Офисы и бизнес-центры",
    image: "/assets/images/aroma-office.jpg",
    desc: "Создание уникальной атмосферы для сотрудников и гостей, повышение лояльности и продуктивности."
  },
  {
    title: "Бутики и магазины",
    image: "/assets/images/aroma-boutique.jpg",
    desc: "Запоминающийся аромат бренда, увеличение времени пребывания и продаж."
  },
  {
    title: "Отели и рестораны",
    image: "/assets/images/aroma-hotel.jpg",
    desc: "Гостеприимная атмосфера, индивидуальные решения для разных зон."
  }
];

export const AromaServicesPage: React.FC = () => {
  const location = useLocation();
  const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } }
  };
  const cardsContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.18 } }
  };
  const cardItem = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <>
      <Helmet>
        <title>Ароматизация помещений — SAFONOVA</title>
        <meta name="description" content="Профессиональные решения для создания фирменного аромата в офисах, магазинах и отелях." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/aroma-services'}`} />
        <meta property="og:title" content="Ароматизация помещений — SAFONOVA" />
        <meta property="og:description" content="Профессиональные решения для создания фирменного аромата в офисах, магазинах и отелях." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/aroma-services'}`} />
        <meta property="og:image" content={`${BASE_URL || ''}/og-default.png`} />
      </Helmet>
      {/* Hero Section */}
      <motion.section
        className="relative flex items-center py-32 md:py-40 bg-gradient-to-br from-primary/10 to-background"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="container mx-auto px-6 flex flex-col items-start">
          <motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-foreground">
              Ароматизация <span className="font-semibold">помещений</span>
            </h1>
            <p className="text-lg md:text-xl text-foreground/80 mb-8 max-w-xl">
              Профессиональные решения для создания фирменного аромата в офисах, магазинах, отелях и других пространствах.
            </p>
            <Button
              as={Link}
              to="/contacts"
              color="primary"
              size="lg"
              className="font-medium"
              endContent={<Icon icon="lucide:mail" width={18} />}
            >
              Оставить заявку
            </Button>
          </motion.div>
        </div>
      </motion.section>

      {/* Преимущества */}
      <motion.section
        className="py-20 bg-background"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="grid md:grid-cols-3 gap-8 mb-16"
            variants={cardsContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div variants={cardItem}>
              <Card className="border border-divider bg-content2/60" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:star" width={36} className="mb-4 text-primary" />
                  <h3 className="text-lg font-medium mb-2">Индивидуальный подбор аромата</h3>
                  <p className="text-foreground/70">Создаём уникальные композиции под задачи вашего бизнеса и особенности пространства.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider bg-content2/60" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:wind" width={36} className="mb-4 text-primary" />
                  <h3 className="text-lg font-medium mb-2">Современное оборудование</h3>
                  <p className="text-foreground/70">Профессиональные диффузоры и системы ароматизации для любых площадей.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider bg-content2/60" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:leaf" width={36} className="mb-4 text-primary" />
                  <h3 className="text-lg font-medium mb-2">Безопасные ингредиенты</h3>
                  <p className="text-foreground/70">Используем только сертифицированные масла и аромакомпозиции.</p>
                </CardBody>
              </Card>
            </motion.div>
          </motion.div>

          <motion.div
            className="text-center mb-12"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4">Примеры <span className="font-medium">реализаций</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Мы реализуем проекты любой сложности — от небольших бутиков до крупных бизнес-центров.
            </p>
          </motion.div>
          <motion.div
            className="grid md:grid-cols-3 gap-8"
            variants={cardsContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {aromaExamples.map((ex, i) => (
              <motion.div key={i} variants={cardItem}>
                <Card className="border border-divider bg-content2/80" disableRipple>
                  <CardBody className="p-6 flex flex-col items-center text-center">
                    <img src={ex.image} alt={ex.title} className="mb-4 rounded-lg shadow w-full h-48 object-cover" />
                    <h4 className="text-lg font-medium mb-2">{ex.title}</h4>
                    <p className="text-foreground/70">{ex.desc}</p>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Как заказать */}
      <motion.section
        className="py-20 bg-content2"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4">Как <span className="font-medium">заказать</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Сопровождаем вас на каждом этапе — от подбора аромата до обслуживания оборудования.
            </p>
          </motion.div>
          <motion.div
            className="grid md:grid-cols-4 gap-8"
            variants={cardsContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div variants={cardItem}>
              <Card className="border border-divider" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:message-circle" width={32} className="mb-3 text-primary" />
                  <h4 className="font-medium mb-1">Заявка</h4>
                  <p className="text-foreground/70 text-sm">Оставьте заявку или свяжитесь с нами для консультации.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:palette" width={32} className="mb-3 text-primary" />
                  <h4 className="font-medium mb-1">Подбор аромата</h4>
                  <p className="text-foreground/70 text-sm">Обсудим задачи, предложим варианты, проведём тестирование.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:factory" width={32} className="mb-3 text-primary" />
                  <h4 className="font-medium mb-1">Монтаж</h4>
                  <p className="text-foreground/70 text-sm">Установим оборудование, обучим персонал.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:truck" width={32} className="mb-3 text-primary" />
                  <h4 className="font-medium mb-1">Сервис</h4>
                  <p className="text-foreground/70 text-sm">Обеспечим регулярную поставку расходников и сервисное обслуживание.</p>
                </CardBody>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Contact form section (placeholder) */}
      <motion.section
        className="py-20 bg-background"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="container mx-auto px-6 max-w-2xl">
          <motion.div
            className="text-center mb-8"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4">Оставить <span className="font-medium">заявку</span></h2>
            <p className="text-foreground/70">
              Заполните форму, и мы свяжемся с вами для обсуждения деталей и подготовки индивидуального предложения.
            </p>
          </motion.div>
          {/* Здесь может быть форма обратной связи или импорт готового компонента */}
          <motion.div
            className="bg-content2/60 rounded-xl p-8 text-center text-foreground/60"
            variants={cardItem}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            [Форма обратной связи будет здесь]
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

export default AromaServicesPage;
